import { borrowMarketsQueryParams } from '@/config/query/queryParams'
import {
  SELECTED_COLLATERALS_KEY,
  SELECTED_NETWORKS_KEY,
  SELECTED_PROTOCOLS_KEY,
  SELECTED_TOKENS_KEY,
} from '@/constants/state'
import { useMixpanel } from '@/context/mixpanel'
import type { BorrowMarketRow } from '@/hooks/useBorrowMarkets'
import { useBorrowMarketsSearchState } from '@/hooks/useBorrowMarketsSearchState'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getProtocolDisplayName } from '@/utils/protocol'
import { getGroupedTokens } from '@/utils/tokens/getGroupedTokens'
import { getOverwrittenTokenSymbol } from '@/utils/tokens/getOverwrittenTokenSymbol'
import { getNetworkImage, getProtocolImage, getTokenImage } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import { RoundImage } from '../common/roundImage'

export const useBorrowMarketsFilterData = (borrowMarkets: BorrowMarketRow[]) => {
  const { selectedNetworks, selectedProtocols, selectedTokens, selectedCollaterals, updateSearchState } =
    useBorrowMarketsSearchState()
  const { trackEvent } = useMixpanel()
  const filterableBorrowMarkets = useMemo(
    () =>
      borrowMarkets.filter(
        (borrowMarket) =>
          borrowMarket.marketSizeUsd !== undefined && borrowMarket.marketSizeUsd >= borrowMarketsQueryParams.minTvl
      ),
    [borrowMarkets]
  )
  const networks = useMemo(() => {
    const uniqueNetworks = [
      ...new Set(filterableBorrowMarkets.map((borrowMarket) => borrowMarket.networkName.toLowerCase())),
    ]
    return uniqueNetworks.sort((a, b) => getNetworkName(a).localeCompare(getNetworkName(b)))
  }, [filterableBorrowMarkets])

  const protocols = useMemo(() => {
    const uniqueProtocols = [
      ...new Set(filterableBorrowMarkets.map((borrowMarket) => borrowMarket.protocolName.toLowerCase())),
    ]
    return uniqueProtocols.sort()
  }, [filterableBorrowMarkets])
  const protocolDisplayNames = useMemo(
    () =>
      Object.fromEntries(
        filterableBorrowMarkets.map((borrowMarket) => [
          borrowMarket.protocolName.toLowerCase(),
          getProtocolDisplayName(borrowMarket.market.protocol),
        ])
      ),
    [filterableBorrowMarkets]
  )

  const tokens = useMemo(() => {
    const uniqueTokens = [...new Set(filterableBorrowMarkets.map((borrowMarket) => borrowMarket.tokenSymbol))]
    return uniqueTokens.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
  }, [filterableBorrowMarkets])

  const groupedTokens = useMemo(() => {
    return getGroupedTokenFilterItems(tokens)
  }, [tokens])

  const collaterals = useMemo(() => {
    const uniqueCollaterals = new Set(
      filterableBorrowMarkets.flatMap((borrowMarket) =>
        borrowMarket.collateralAssets.map((assetData) => assetData.asset.symbol)
      )
    )
    return [...uniqueCollaterals].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
  }, [filterableBorrowMarkets])

  const groupedCollaterals = useMemo(() => {
    return getGroupedTokenFilterItems(collaterals)
  }, [collaterals])

  const renderTokenPrefix = (item: string) => (
    <RoundImage
      src={getTokenImage(item)}
      fallback={getTokenImage('fallback')}
      alt={`${item} token`}
      className="size-7"
    />
  )

  return {
    tokenFilterProps: {
      groupedItems: groupedTokens,
      selectedItems: selectedTokens,
      setSelectedItems: (value: string[]) => {
        updateSearchState([SELECTED_TOKENS_KEY, value])
        trackEvent('Filter used', { Filter: 'token', Value: value.join(', ') })
      },
      name: 'tokens',
      renderItemPrefix: renderTokenPrefix,
      searchNormalizer: getOverwrittenTokenSymbol,
    },
    collateralFilterProps: {
      groupedItems: groupedCollaterals,
      selectedItems: selectedCollaterals,
      setSelectedItems: (value: string[]) => {
        updateSearchState([SELECTED_COLLATERALS_KEY, value])
        trackEvent('Filter used', { Filter: 'collateral', Value: value.join(', ') })
      },
      name: 'collateral',
      renderItemPrefix: renderTokenPrefix,
      searchNormalizer: getOverwrittenTokenSymbol,
    },
    networkFilterProps: {
      items: networks,
      selectedItems: selectedNetworks,
      setSelectedItems: (value: string[]) => {
        updateSearchState([SELECTED_NETWORKS_KEY, value])
        trackEvent('Filter used', { Filter: 'network', Value: value.join(', ') })
      },
      name: 'network',
      renderItemPrefix: (item: string) => (
        <RoundImage
          src={getNetworkImage(item)}
          fallback={getNetworkImage('fallback')}
          alt={`${item} network`}
          className="size-7"
        />
      ),
      searchNormalizer: (name: string) => `${name.toLowerCase()} ${getNetworkName(name.toLowerCase()).toLowerCase()}`,
      itemFormatter: getNetworkName,
      capitalize: true,
    },
    protocolFilterProps: {
      items: protocols,
      selectedItems: selectedProtocols,
      setSelectedItems: (value: string[]) => {
        updateSearchState([SELECTED_PROTOCOLS_KEY, value])
        trackEvent('Filter used', { Filter: 'protocol', Value: value.join(', ') })
      },
      name: 'protocol',
      renderItemPrefix: (item: string) => (
        <RoundImage
          src={getProtocolImage(item)}
          fallback={getProtocolImage('fallback')}
          alt={`${protocolDisplayNames[item] ?? item} protocol`}
          className="size-7"
        />
      ),
      itemFormatter: (item: string) => protocolDisplayNames[item] ?? item,
      searchNormalizer: (item: string) => `${item} ${protocolDisplayNames[item]?.toLowerCase() ?? ''}`,
    },
  }
}

const getGroupedTokenFilterItems = (tokens: string[]) => {
  const { ethTokens, usdCoins, euroCoins, btcCoins, otherTokens } = getGroupedTokens(tokens)

  return {
    ETH: { items: ethTokens, isSelectable: true },
    USD: { items: usdCoins, isSelectable: true },
    EURO: { items: euroCoins, isSelectable: true },
    BTC: { items: btcCoins, isSelectable: true },
    Other: { items: otherTokens, isSelectable: false },
  }
}
